import { CascadiaInternalButtonProps } from "@certn/cascadia/components/button/types";
import { rem } from "@certn/cascadia/utils";
import {
  CascadiaMiniIcon,
  CascadiaSpacing,
  CascadiaStandardIcon,
  CascadiaCustomIcon,
  shapes,
  spacing,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { RefObject, useRef } from "react";
import { ButtonProps as AriaButtonProps, Button } from "react-aria-components";

import { IconButtonProps } from "./_IconButton";
import { BUTTON_SIZES } from "./constants";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { Loader } from "../loaders/loading/Loader";
import { accessibleTouchSize, useGetAllFocusShadows } from "../utils";
import { useColors } from "../../hooks/useColors";
export interface ButtonProps
  extends Omit<AriaButtonProps, "children" | "onPress">,
    Omit<CascadiaInternalButtonProps, "leftIcon" | "rightIcon"> {
  fullWidth?: boolean;
  leftIcon?: CascadiaMiniIcon | CascadiaStandardIcon | CascadiaCustomIcon;
  rightIcon?: CascadiaMiniIcon | CascadiaStandardIcon;
  buttonRef?: RefObject<HTMLButtonElement>;

  testID?: string;
  iconSize?: CascadiaSpacing;
  iconFillOverride?: boolean;
  iconType?: "mini" | "standard" | "custom" | undefined;
}

export function _Button(props: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const {
    border,
    color,
    fill,
    hoverFill,
    shape = "border-radius-2",
    isLoading,
    buttonRef = ref,
    size = "medium",
    children,
    testID = "button",
    fullWidth,
    leftIcon,
    rightIcon,
    type = "button",
    iconType = "mini",
    className,
    ...buttonProps
  } = props;
  const colors = useColors();
  const sizeTokens = BUTTON_SIZES[size];
  const boxShadow = border ? `inset 0 0 0 1px ${colors[border]}` : undefined;
  const getAllFocusShadows = useGetAllFocusShadows();

  return (
    <Button
      isDisabled={props.isDisabled || props.isLoading}
      {...buttonProps}
      type={type}
      className={css(
        styles.button,
        getSizeStyles({
          size,
          fullWidth,
          leftIcon,
          rightIcon,
          children,
        }),
        css(
          {
            background: fill ? colors[fill] : "transparent",
            border: "none",
            outline: "none",
            overflow: "hidden",
            color: color ? colors[color] : undefined,
            borderRadius: shapes[shape],
            "&:focus-within": getAllFocusShadows(boxShadow),
            boxShadow,
            "&:hover:enabled": {
              "> span": {
                color: "undefined",
              },
              background: hoverFill ? colors[hoverFill] : "undefined",
            },
            "&:disabled": {
              opacity: isLoading ? 1 : 0.6,
              cursor: isLoading ? "progress" : "not-allowed",
            },
          },
          isLoading && {
            "span, > i": { color: "transparent", fill: "transparent" },
          }
        ),
        styles.button,
        typeof className === "string" ? className : undefined
      )}
      data-testid={testID}
      ref={buttonRef}
    >
      {(renderProps) => {
        return (
          <>
            {props.leftIcon && (
              <Icon
                type={iconType}
                name={props.leftIcon}
                color={props.iconColor || props.color}
                size={props.iconSize || sizeTokens.iconSize}
                fillOverride={props.iconFillOverride}
              />
            )}
            <Text style={sizeTokens.textStyle} color={props.color}>
              {children}
            </Text>
            {props.rightIcon && (
              <Icon
                type={iconType}
                name={props.rightIcon}
                color={props.iconColor || props.color}
                size={props.iconSize || sizeTokens.iconSize}
                fillOverride={props.iconFillOverride}
              />
            )}
            {props.isLoading && (
              <Loader
                overlay
                size="medium"
                color={props.loaderColor || props.color}
              />
            )}
          </>
        );
      }}
    </Button>
  );
}

const styles = {
  button: css({
    position: "relative",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:after": accessibleTouchSize,
  }),
};

const getSizeStyles = ({
  size = "medium",
  fullWidth,
  leftIcon,
  rightIcon,
  children,
}: Partial<ButtonProps & IconButtonProps>) => {
  const sizeTokens = BUTTON_SIZES[size];
  return {
    paddingLeft:
      leftIcon && children
        ? sizeTokens.paddingIcon
        : sizeTokens.paddingHorizontal,
    paddingRight:
      rightIcon && children
        ? sizeTokens.paddingIcon
        : sizeTokens.paddingHorizontal,
    paddingTop: sizeTokens.paddingVertical,
    paddingBottom: sizeTokens.paddingVertical,
    minHeight: rem(sizeTokens.minSize),
    minWidth: rem(sizeTokens.minSize),
    "> :not(:last-child)": {
      marginRight: `${spacing["space-1"].rem}rem`,
    },
    width: fullWidth ? "100%" : "fit-content",
  };
};
